import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import axios from 'axios';
import { SnackbarProvider } from 'notistack';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginForm from './components/LoginForm';
import SupportDialog from './components/common/SupportDialog';
import SystemStatusIndicator from './components/common/SystemStatusIndicator';
import ConstructionIcon from '@mui/icons-material/Construction';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { API_BASE_URL } from './config/api';
import {
  recordStandaloneProductEvent,
  setProductContext
} from './utils/quoteFlowTelemetry';
import './App.css';
import './components/ProjectReview.css';
import './styles/designSystem.css';

// Configure axios base URL for API requests
if (API_BASE_URL && API_BASE_URL !== '') {
  axios.defaults.baseURL = API_BASE_URL;
}

// Lazy load heavy components for better initial load performance
const ProjectFlow = lazy(() => import('./components/ProjectFlow'));
const MaterialsList = lazy(() => import('./MaterialsList'));
const PlanningDashboard = lazy(() => import('./components/PlanningDashboard'));
const NoeglatalDashboard = lazy(() => import('./components/NoeglatalDashboard'));
const AdvancedDashboard = lazy(() => import('./components/AdvancedDashboard'));
const SmartPackagesRoutes = lazy(() => import('./routes/SmartPackagesRoutes'));
const MobileOrders = lazy(() => import('./components/MobileOrders'));
const UsersManagement = lazy(() => import('./components/UsersManagement'));
const OrdrestyringSyncDashboard = lazy(() => import('./components/OrdrestyringSyncDashboard'));

const DEFAULT_VIEW = 'projects';
const APP_LOAD_RECORDED_KEY = 'tilbudgivern_app_load_recorded';

const VIEW_CONFIG = {
  projects: {
    icon: ConstructionIcon,
    label: 'Projekt Flow',
    description: 'Opret tilbud fra projektdata, geometri, smart pakker og final review.'
  },
  planning: {
    icon: AssignmentOutlinedIcon,
    label: 'Planlægning',
    description: 'Følg ordrer, kalender og færdige tilbud i samme arbejdsflade.'
  },
  mobile: {
    icon: PhoneAndroidOutlinedIcon,
    label: 'Besigtigelse',
    description: 'Saml mål, billeder og feltnoter hos kunden, og gør sagen klar til tilbud.'
  },
  materials: {
    icon: Inventory2OutlinedIcon,
    label: 'Materialer',
    description: 'Administrer materialer, import og prisgrundlag.'
  },
  'smart-packages': {
    icon: WidgetsOutlinedIcon,
    label: 'Smart Pakker',
    description: 'Vedligehold pakker, opgaver, materialer og genbrugelige skabeloner.'
  },
  'openai-usage': {
    icon: SmartToyOutlinedIcon,
    label: 'AI Budget',
    description: 'Se reelt OpenAI-forbrug fra organization API og lokal usage tracking.'
  },
  noegletal: {
    icon: '📈',
    label: 'Nøgletal',
    description: 'Ordrestyring-nøgletal og realtidsmålinger.'
  },
  dashboard: {
    icon: '📊',
    label: 'Dashboard',
    description: 'Systemmålinger og avanceret driftsblik.'
  },
  users: {
    icon: PeopleAltOutlinedIcon,
    label: 'Brugere',
    description: 'Administrer login til systemet (kun admin).'
  },
  ordrestyring: {
    icon: SyncOutlinedIcon,
    label: 'Ordrestyring',
    description: 'Følg synkroniseringsstatus og friskhed for det lokale dataspejl.'
  }
};

const NAV_VIEWS = ['projects', 'planning', 'mobile', 'materials', 'smart-packages', 'openai-usage'];

export const getNavigationViews = (user) => (
  user?.role === 'admin' ? [...NAV_VIEWS, 'ordrestyring', 'users'] : NAV_VIEWS
);

const getInitialView = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_VIEW;
  }

  try {
    const savedView = window.localStorage.getItem('tilbudgivern_current_view');
    return VIEW_CONFIG[savedView] ? savedView : DEFAULT_VIEW;
  } catch {
    return DEFAULT_VIEW;
  }
};

export const normalizeViewForRole = (viewId, user) => (
  ['users', 'ordrestyring'].includes(viewId) && user?.role !== 'admin' ? DEFAULT_VIEW : viewId
);

// Loading fallback component
const LoadingFallback = () => (
  <div className="loading-fallback">
    <div className="loading-spinner"></div>
    <p>Indlæser...</p>
  </div>
);

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const toOptionalNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatNumber = (value, fallback = '0') => {
  const number = toOptionalNumber(value);
  return number === null ? fallback : number.toLocaleString('da-DK');
};

const formatSmartUsd = (value) => {
  const cost = toNumber(value);
  return cost.toFixed(4).replace(/\.?0+$/, '') || '0';
};

const normalizeUsageMetric = (metric = {}) => ({
  label: metric.label || '',
  description: metric.description || '',
  requestCount: toNumber(metric.requestCount),
  totalTokens: toNumber(metric.totalTokens),
  promptTokens: toNumber(metric.promptTokens),
  completionTokens: toNumber(metric.completionTokens),
  totalCost: toNumber(metric.totalCost),
  lastUsage: metric.lastUsage || null
});

const normalizeUsageDashboard = (dashboard = {}) => {
  const normalizePeriod = (period = {}) => {
    const categories = period.categories || {};
    return {
      label: period.label || '',
      startsAt: period.startsAt || null,
      categories: {
        quote_generation: normalizeUsageMetric(categories.quote_generation),
        site_build: normalizeUsageMetric(categories.site_build),
        other: normalizeUsageMetric(categories.other)
      }
    };
  };

  return {
    source: dashboard.source || 'openai_usage_stats',
    generatedAt: dashboard.generatedAt || null,
    coverageNote: dashboard.coverageNote || '',
    periods: {
      currentMonth: normalizePeriod(dashboard.periods?.currentMonth),
      last30Days: normalizePeriod(dashboard.periods?.last30Days),
      allTime: normalizePeriod(dashboard.periods?.allTime)
    },
    breakdown: Array.isArray(dashboard.breakdown)
      ? dashboard.breakdown.map((row) => ({
        category: row.category || 'other',
        requestType: row.requestType || 'unknown',
        modelUsed: row.modelUsed || 'unknown',
        requestCount: toNumber(row.requestCount),
        totalTokens: toNumber(row.totalTokens),
        promptTokens: toNumber(row.promptTokens),
        completionTokens: toNumber(row.completionTokens),
        totalCost: toNumber(row.totalCost),
        lastUsage: row.lastUsage || null
      }))
      : []
  };
};

const normalizeOrganizationUsage = (usage = {}) => {
  const normalizeMetric = (metric = {}) => ({
    available: Boolean(metric.available),
    status: metric.status || null,
    requests: toNumber(metric.requests),
    inputTokens: toNumber(metric.inputTokens),
    cachedInputTokens: toNumber(metric.cachedInputTokens),
    outputTokens: toNumber(metric.outputTokens),
    totalTokens: toNumber(metric.totalTokens),
    totalCost: toNumber(metric.totalCost),
    resultCount: toNumber(metric.resultCount),
    bucketCount: toNumber(metric.bucketCount),
    error: metric.error || ''
  });

  return {
    available: Boolean(usage.available),
    source: usage.source || 'openai_organization_api',
    period: {
      daysBack: toNumber(usage.period?.daysBack, 30),
      start: usage.period?.start || null,
      end: usage.period?.end || null
    },
    summary: {
      totalCost: toNumber(usage.summary?.totalCost),
      totalRequests: toNumber(usage.summary?.totalRequests),
      totalTokens: toNumber(usage.summary?.totalTokens),
      projectCount: toNumber(usage.summary?.projectCount)
    },
    costs: {
      available: Boolean(usage.costs?.available),
      status: usage.costs?.status || null,
      totalCost: toNumber(usage.costs?.totalCost),
      bucketCount: toNumber(usage.costs?.bucketCount),
      resultCount: toNumber(usage.costs?.resultCount),
      hasMore: Boolean(usage.costs?.hasMore),
      error: usage.costs?.error || ''
    },
    metrics: {
      completions: normalizeMetric(usage.metrics?.completions),
      embeddings: normalizeMetric(usage.metrics?.embeddings),
      images: normalizeMetric(usage.metrics?.images),
      audio_speeches: normalizeMetric(usage.metrics?.audio_speeches),
      audio_transcriptions: normalizeMetric(usage.metrics?.audio_transcriptions),
      vector_stores: normalizeMetric(usage.metrics?.vector_stores)
    },
    projects: {
      available: Boolean(usage.projects?.available),
      status: usage.projects?.status || null,
      count: toNumber(usage.projects?.count),
      hasMore: Boolean(usage.projects?.hasMore),
      error: usage.projects?.error || ''
    },
    note: usage.note || ''
  };
};

const normalizeSubscriptionUsage = (usage = {}) => {
  const normalizeTotals = (value = {}) => ({
    runs: toNumber(value.runs),
    completedRuns: toNumber(value.completedRuns),
    failedRuns: toNumber(value.failedRuns),
    activeRuns: toNumber(value.activeRuns),
    inputTokens: toNumber(value.inputTokens),
    cachedInputTokens: toNumber(value.cachedInputTokens),
    outputTokens: toNumber(value.outputTokens),
    totalTokens: toNumber(value.totalTokens),
    averageDurationSeconds: toNumber(value.averageDurationSeconds),
    models: Array.isArray(value.models) ? value.models : [],
    reasoningEfforts: Array.isArray(value.reasoningEfforts) ? value.reasoningEfforts : [],
    lastRun: value.lastRun || null
  });
  return {
    source: usage.source || 'ai_jobs',
    provider: usage.provider || 'Codex via ChatGPT-abonnement',
    periodDays: toNumber(usage.periodDays, 30),
    generatedAt: usage.generatedAt || null,
    note: usage.note || '',
    summary: {
      ...normalizeTotals(usage.summary),
      featureCount: toNumber(usage.summary?.featureCount, 11),
      usedFeatureCount: toNumber(usage.summary?.usedFeatureCount)
    },
    features: Array.isArray(usage.features)
      ? usage.features.map(feature => ({
        key: feature.key || 'unknown',
        label: feature.label || feature.key || 'Ukendt AI-funktion',
        ...normalizeTotals(feature)
      }))
      : []
  };
};

const normalizeMonthlyBudget = (budget = {}, metadata = {}) => ({
  available: metadata.success === true,
  monthlyBudget: toNumber(budget.monthlyBudget),
  currentCost: toNumber(budget.currentCost),
  remainingBudget: toNumber(budget.remainingBudget),
  costPercent: Math.max(0, Math.min(100, toNumber(budget.costPercent))),
  warningLevel: budget.warningLevel || 'ok',
  message: budget.message || 'Budget i orden',
  dataSource: metadata.dataSource || 'database',
  updatedAt: metadata.timestamp || null
});

function AppContent() {
  const [currentView, setCurrentView] = useState(getInitialView); // 'projects', 'planning', 'materials', 'noegletal', 'smart-packages', 'openai-usage', 'dashboard'
  const { user, isAuthenticated, loading, logout, sessionWarning } = useAuth();
  const [logoutPending, setLogoutPending] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const handleLogout = async () => {
    if (logoutPending) return;
    setLogoutPending(true);
    setLogoutError('');
    try {
      const result = await logout();
      if (!result.success) setLogoutError(result.error);
    } catch (_error) {
      setLogoutError('Du kunne ikke logges ud. Prøv igen.');
    } finally { setLogoutPending(false); }
  };
  const navViews = getNavigationViews(user);
  const [supportOpen, setSupportOpen] = useState(false);
  const [navigationOpen, setNavigationOpen] = useState(false);

  const [usageDashboard, setUsageDashboard] = useState(null);
  const [organizationUsage, setOrganizationUsage] = useState(null);
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);
  const [monthlyBudget, setMonthlyBudget] = useState(null);
  const [openaiUsageLoading, setOpenaiUsageLoading] = useState(false);
  const [openaiUsageError, setOpenaiUsageError] = useState('');

  useEffect(() => {
    if (loading || !isAuthenticated) return;
    setCurrentView((viewId) => normalizeViewForRole(viewId, user));
  }, [isAuthenticated, loading, user]);

  const loadOpenaiUsage = useCallback(async () => {
    const fetchUsageEndpoint = async (label, url, timeout = 10000) => {
      try {
        const response = await axios.get(url, { timeout });
        return { label, ok: true, data: response.data };
      } catch (error) {
        return {
          label,
          ok: false,
          status: error.response?.status,
          message: error.response?.data?.error || error.message,
          data: error.response?.data
        };
      }
    };

    setOpenaiUsageLoading(true);
    setOpenaiUsageError('');
    setUsageDashboard((currentDashboard) => currentDashboard || normalizeUsageDashboard());
    setOrganizationUsage((currentUsage) => currentUsage || normalizeOrganizationUsage());
    setSubscriptionUsage((currentUsage) => currentUsage || normalizeSubscriptionUsage());
    setMonthlyBudget((currentBudget) => currentBudget || normalizeMonthlyBudget());

    try {
      const [monthlyBudgetResult, subscriptionUsageResult, usageDashboardResult, organizationUsageResult] = await Promise.all([
        fetchUsageEndpoint('Månedsbudget', `${API_BASE_URL}/api/quotes/openai/budget/status`, 20000),
        fetchUsageEndpoint('Codex-abonnement', `${API_BASE_URL}/api/ai/subscription-usage?daysBack=30`),
        fetchUsageEndpoint('Usage dashboard', `${API_BASE_URL}/api/quotes/openai/usage-dashboard`),
        fetchUsageEndpoint('OpenAI organization usage', `${API_BASE_URL}/api/quotes/openai/organization-usage?daysBack=30`, 20000)
      ]);

      const monthlyBudgetData = monthlyBudgetResult.data || {};
      const subscriptionUsageData = subscriptionUsageResult.data || {};
      const usageDashboardData = usageDashboardResult.data || {};
      const organizationUsageData = organizationUsageResult.data || {};

      if (monthlyBudgetData?.success && monthlyBudgetData?.budget) {
        setMonthlyBudget(normalizeMonthlyBudget(monthlyBudgetData.budget, monthlyBudgetData));
      }
      if (subscriptionUsageData?.success && subscriptionUsageData?.usage) {
        setSubscriptionUsage(normalizeSubscriptionUsage(subscriptionUsageData.usage));
      }
      if (usageDashboardData?.success && usageDashboardData?.usageDashboard) {
        setUsageDashboard(normalizeUsageDashboard(usageDashboardData.usageDashboard));
      }
      if (organizationUsageData?.success && organizationUsageData?.organizationUsage) {
        setOrganizationUsage(normalizeOrganizationUsage(organizationUsageData.organizationUsage));
      }

      const failures = [monthlyBudgetResult, subscriptionUsageResult, usageDashboardResult, organizationUsageResult]
        .filter((result) => !result.ok);
      if (failures.length > 0) {
        const failureSummary = failures
          .map((result) => `${result.label}${result.status ? ` (${result.status})` : ''}: ${result.message}`)
          .join(' | ');
        setOpenaiUsageError(`Nogle reelle AI-usage data kunne ikke hentes. ${failureSummary}`);
      }
    } catch (error) {
      console.error('Error loading OpenAI usage:', error);
      setUsageDashboard(normalizeUsageDashboard());
      setOrganizationUsage(normalizeOrganizationUsage());
      setSubscriptionUsage(normalizeSubscriptionUsage());
      setMonthlyBudget(normalizeMonthlyBudget());
      setOpenaiUsageError('AI-usage data kunne ikke hentes lige nu.');
    } finally {
      setOpenaiUsageLoading(false);
    }
  }, []);

  // Check authentication on mount
  useEffect(() => {
    if (isAuthenticated && currentView === 'openai-usage') {
      // Load AI usage data when the budget view is active
      loadOpenaiUsage();
    }
    
    // Set up global AI usage refresh callback
    window.refreshAIUsage = loadOpenaiUsage;
    
    // Cleanup on unmount
    return () => {
      delete window.refreshAIUsage;
    };
  }, [isAuthenticated, currentView, loadOpenaiUsage]);

  useEffect(() => {
    try {
      if (VIEW_CONFIG[currentView]) {
        window.localStorage.setItem('tilbudgivern_current_view', currentView);
      }
    } catch {
      // Browser privacy settings can block localStorage; the app can run without persistence.
    }
  }, [currentView]);

  useEffect(() => {
    setProductContext({
      viewId: currentView,
      viewLabel: VIEW_CONFIG[currentView]?.label || currentView
    });
  }, [currentView]);

  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      if (window.sessionStorage.getItem(APP_LOAD_RECORDED_KEY)) return;
      window.sessionStorage.setItem(APP_LOAD_RECORDED_KEY, 'true');
    } catch {
      // Continue with a best-effort measurement when storage is unavailable.
    }
    const bootStartedAt = Number(window.__tilbudgivernBootStartedAt) || 0;
    recordStandaloneProductEvent({
      apiBaseUrl: API_BASE_URL,
      eventType: 'app_loaded',
      elapsedMs: Math.max(0, Math.round((window.performance?.now?.() || 0) - bootStartedAt)),
      metadata: {
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight
      }
    });
  }, [isAuthenticated]);

  // Auth handlers moved to LoginForm component

  const handleViewChange = (viewId) => {
    setCurrentView(viewId);
    setNavigationOpen(false);
  };

  const organizationMetricRows = organizationUsage ? [
    { key: 'costs', label: 'Costs', metric: organizationUsage.costs, note: `${organizationUsage.costs.bucketCount} buckets` },
    { key: 'completions', label: 'Completions', metric: organizationUsage.metrics.completions },
    { key: 'embeddings', label: 'Embeddings', metric: organizationUsage.metrics.embeddings },
    { key: 'images', label: 'Images', metric: organizationUsage.metrics.images },
    { key: 'audio_speeches', label: 'Audio speeches', metric: organizationUsage.metrics.audio_speeches },
    { key: 'audio_transcriptions', label: 'Audio transcriptions', metric: organizationUsage.metrics.audio_transcriptions },
    { key: 'vector_stores', label: 'Vector stores', metric: organizationUsage.metrics.vector_stores },
    { key: 'projects', label: 'Projects', metric: organizationUsage.projects, note: `${organizationUsage.projects.count} projects` }
  ] : [];

  if (loading) {
    return (
      <SnackbarProvider 
        maxSnack={3} 
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        autoHideDuration={4000}
      >
        <LoadingFallback />
      </SnackbarProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <SnackbarProvider 
        maxSnack={3} 
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        autoHideDuration={4000}
      >
        <LoginForm />
      </SnackbarProvider>
    );
  }

  const navigateToCompletedQuotes = () => {
    setCurrentView('planning');
  };

  const currentViewConfig = VIEW_CONFIG[currentView] || VIEW_CONFIG[DEFAULT_VIEW];

  return (
    <SnackbarProvider 
      maxSnack={3} 
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      autoHideDuration={4000}
    >
      <div className={`App view-${currentView} ${currentView === 'materials' ? 'materials-mode' : ''}`}>
        <a className="skip-link" href="#main-content">Gå til hovedindhold</a>
        <header className="header">
          <div className="app-header-topline">
            <div className="app-brand">
              <span className="app-brand-icon" aria-hidden="true"><ConstructionIcon /></span>
              <div>
                <h1>Tilbudgivern</h1>
                <p>{currentViewConfig.description}</p>
              </div>
            </div>

            {isAuthenticated && (
              <div className="app-header-actions">
                <SystemStatusIndicator apiBaseUrl={API_BASE_URL} />
                <button type="button" className="support-btn" aria-label="Send SOS eller kontakt support" onClick={() => setSupportOpen(true)}>
                  <SupportAgentOutlinedIcon aria-hidden="true" />
                  <span>SOS / Support</span>
                </button>
                <div>
                  <button type="button" className="logout-btn" aria-label={logoutError ? 'Prøv igen' : 'Log ud'} disabled={logoutPending} onClick={handleLogout}>
                    <LogoutRoundedIcon aria-hidden="true" />
                    <span>{logoutPending ? 'Logger ud…' : logoutError ? 'Prøv igen' : 'Log ud'}</span>
                  </button>
                  {logoutError && <p role="alert">{logoutError}</p>}
                  {sessionWarning && <p role="alert">{sessionWarning}</p>}
                </div>
                <button
                  type="button"
                  className="mobile-menu-btn"
                  aria-expanded={navigationOpen}
                  aria-controls="primary-navigation"
                  onClick={() => setNavigationOpen(open => !open)}
                >
                  {navigationOpen ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
                  <span>Menu</span>
                </button>
              </div>
            )}
          </div>
          
          {isAuthenticated && (
            <div className={`app-nav-shell ${navigationOpen ? 'is-open' : ''}`} id="primary-navigation">
              <nav className="nav-buttons" aria-label="Hovednavigation">
                {navViews.map((viewId) => {
                  const view = VIEW_CONFIG[viewId];
                  const isActive = currentView === viewId;
                  const ViewIcon = view.icon;

                  return (
                    <button
                      key={viewId}
                      type="button"
                      className={`nav-btn ${isActive ? 'active' : ''}`}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => handleViewChange(viewId)}
                    >
                      <ViewIcon aria-hidden="true" />
                      <span>{view.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          )}
      </header>

      <SupportDialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        context={{ viewId: currentView, viewLabel: currentViewConfig.label }}
      />

      <main className="main-content" id="main-content">
        <Suspense fallback={<LoadingFallback />}>
          
          {currentView === 'projects' ? (
            // Structured project flow
            <ProjectFlow
              apiBaseUrl={API_BASE_URL}
              onNavigateToCompletedQuotes={navigateToCompletedQuotes}
            />
          ) : currentView === 'materials' ? (
            // Materials Management with new design
            <MaterialsList apiBaseUrl={API_BASE_URL} />
          ) : currentView === 'planning' ? (
            // Planning Dashboard with orders and calendar
            <PlanningDashboard />
          ) : currentView === 'mobile' ? (
            // Mobile field extension for Ordrestyring orders
            <MobileOrders
              apiBaseUrl={API_BASE_URL}
              onStartNewQuote={() => handleViewChange('projects')}
            />
          ) : currentView === 'noegletal' ? (
            // Ordrestyring Noegletal Dashboard with real-time metrics
            <NoeglatalDashboard API_BASE_URL={API_BASE_URL} />
          ) : currentView === 'smart-packages' ? (
            // Smart Packages Management
            <SmartPackagesRoutes initialSection="dashboard" />
          ) : currentView === 'dashboard' ? (
            // Advanced Dashboard with system metrics
            <AdvancedDashboard apiBaseUrl={API_BASE_URL} />
          ) : currentView === 'users' && user?.role === 'admin' ? (
            // User management (admin only)
            <UsersManagement />
          ) : currentView === 'ordrestyring' && user?.role === 'admin' ? (
            <OrdrestyringSyncDashboard />
          ) : currentView === 'openai-usage' ? (
          // OpenAI Usage section
          <div className="openai-usage-view">
            <div className="usage-section">
              <div className="usage-header">
                <div>
                  <h2>🤖 AI Usage</h2>
                  <p>Kun reelle tal fra OpenAI organization API og lokal usage tracking</p>
                </div>
                <button
                  type="button"
                  className="usage-refresh-btn"
                  onClick={loadOpenaiUsage}
                  disabled={openaiUsageLoading}
                >
                  {openaiUsageLoading ? 'Opdaterer...' : 'Opdater'}
                </button>
              </div>

              {monthlyBudget?.available && (
                <section className={`monthly-budget-card ${monthlyBudget.warningLevel}`} aria-label="Månedligt AI-budget">
                  <div className="monthly-budget-heading">
                    <div>
                      <span>Månedsbudget</span>
                      <strong>${formatSmartUsd(monthlyBudget.currentCost)} af ${formatSmartUsd(monthlyBudget.monthlyBudget)}</strong>
                    </div>
                    <div className="monthly-budget-remaining">
                      <span>Tilbage</span>
                      <strong>${formatSmartUsd(monthlyBudget.remainingBudget)}</strong>
                    </div>
                  </div>
                  <div
                    className="monthly-budget-track"
                    role="progressbar"
                    aria-label="Forbrugt månedsbudget"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-valuenow={monthlyBudget.costPercent}
                  >
                    <span style={{ width: `${monthlyBudget.costPercent}%` }} />
                  </div>
                  <div className="monthly-budget-footer">
                    <strong>{monthlyBudget.costPercent.toLocaleString('da-DK')}% brugt</strong>
                    <span>{monthlyBudget.message}</span>
                  </div>
                </section>
              )}

              {openaiUsageError && (
                <div className="usage-warning" role="status">
                  <strong>Delvise data</strong>
                  <span>{openaiUsageError}</span>
                </div>
              )}

              {openaiUsageLoading && (subscriptionUsage || usageDashboard || organizationUsage) && (
                <div className="usage-refreshing" role="status">Opdaterer AI usage...</div>
              )}

              {subscriptionUsage || usageDashboard || organizationUsage ? (
                <div className="usage-tables-stack">
                  {subscriptionUsage && (
                    <section className="usage-data-panel subscription-usage-panel">
                      <div className="usage-panel-heading">
                        <div>
                          <h3>Codex-abonnement</h3>
                          <p>
                            Lokalt registreret træk fra alle AI-funktioner de sidste {subscriptionUsage.periodDays} dage.
                          </p>
                        </div>
                        <span>{subscriptionUsage.provider}</span>
                      </div>

                      <div className="usage-summary-grid subscription-summary-grid">
                        <div>
                          <span>Kørsler</span>
                          <strong>{formatNumber(subscriptionUsage.summary.runs)}</strong>
                        </div>
                        <div>
                          <span>Funktioner brugt</span>
                          <strong>{formatNumber(subscriptionUsage.summary.usedFeatureCount)} / {formatNumber(subscriptionUsage.summary.featureCount)}</strong>
                        </div>
                        <div>
                          <span>Inputtokens</span>
                          <strong>{formatNumber(subscriptionUsage.summary.inputTokens)}</strong>
                        </div>
                        <div>
                          <span>Outputtokens</span>
                          <strong>{formatNumber(subscriptionUsage.summary.outputTokens)}</strong>
                        </div>
                        <div>
                          <span>Cachede inputtokens</span>
                          <strong>{formatNumber(subscriptionUsage.summary.cachedInputTokens)}</strong>
                        </div>
                      </div>

                      <div className="usage-category-table subscription-usage-table">
                        <table>
                          <thead>
                            <tr>
                              <th>AI-funktion</th>
                              <th>Kørsler</th>
                              <th>OK / fejl</th>
                              <th>Input</th>
                              <th>Cachet input</th>
                              <th>Output</th>
                              <th>Model / reasoning</th>
                              <th>Seneste kørsel</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subscriptionUsage.features.map(feature => (
                              <tr key={feature.key}>
                                <td data-label="AI-funktion"><strong>{feature.label}</strong></td>
                                <td data-label="Kørsler">{formatNumber(feature.runs)}</td>
                                <td data-label="OK / fejl">{formatNumber(feature.completedRuns)} / {formatNumber(feature.failedRuns)}</td>
                                <td data-label="Input">{formatNumber(feature.inputTokens)}</td>
                                <td data-label="Cachet input">{formatNumber(feature.cachedInputTokens)}</td>
                                <td data-label="Output"><strong>{formatNumber(feature.outputTokens)}</strong></td>
                                <td data-label="Model / reasoning">
                                  {feature.models.length > 0 ? feature.models.join(', ') : 'Ingen kørsel'}
                                  {feature.reasoningEfforts.length > 0 && <span>{feature.reasoningEfforts.join(', ')}</span>}
                                </td>
                                <td data-label="Seneste kørsel">
                                  {feature.lastRun ? new Date(feature.lastRun).toLocaleString('da-DK') : 'Ikke brugt endnu'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {subscriptionUsage.note && (
                        <p className="usage-dashboard-note">{subscriptionUsage.note}</p>
                      )}
                    </section>
                  )}

                  {organizationUsage && (
                    <section className="usage-data-panel">
                      <div className="usage-panel-heading">
                        <div>
                          <h3>OpenAI organization usage</h3>
                          <p>
                            Rigtig data fra OpenAI organization endpoints, sidste {organizationUsage.period.daysBack} dage.
                          </p>
                        </div>
                        <span>{organizationUsage.available ? 'Tilgængelig' : 'Delvist tilgængelig'}</span>
                      </div>

                      <div className="usage-summary-grid">
                        <div>
                          <span>Total cost</span>
                          <strong>${formatSmartUsd(organizationUsage.summary.totalCost)}</strong>
                        </div>
                        <div>
                          <span>Requests</span>
                          <strong>{formatNumber(organizationUsage.summary.totalRequests)}</strong>
                        </div>
                        <div>
                          <span>Total tokens</span>
                          <strong>{formatNumber(organizationUsage.summary.totalTokens)}</strong>
                        </div>
                        <div>
                          <span>Projects</span>
                          <strong>{formatNumber(organizationUsage.summary.projectCount)}</strong>
                        </div>
                      </div>

                      <div className="usage-category-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Endpoint</th>
                              <th>Status</th>
                              <th>Requests</th>
                              <th>Total tokens</th>
                              <th>Input</th>
                              <th>Cached input</th>
                              <th>Output</th>
                              <th>Cost / note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {organizationMetricRows.map(({ key, label, metric, note }) => (
                              <tr key={key}>
                                <td data-label="Endpoint"><strong>{label}</strong></td>
                                <td data-label="Status">{metric.available ? 'OK' : (metric.status ? `HTTP ${metric.status}` : 'Ingen data')}</td>
                                <td data-label="Requests">{formatNumber(metric.requests)}</td>
                                <td data-label="Total tokens"><strong>{formatNumber(metric.totalTokens)}</strong></td>
                                <td data-label="Input">{formatNumber(metric.inputTokens)}</td>
                                <td data-label="Cached input">{formatNumber(metric.cachedInputTokens)}</td>
                                <td data-label="Output">{formatNumber(metric.outputTokens)}</td>
                                <td data-label="Cost / note">
                                  {key === 'costs' ? `$${formatSmartUsd(metric.totalCost)}` : (note || metric.error || 'OK')}
                                  {metric.error && key !== 'costs' && <span>{metric.error}</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {organizationUsage.note && (
                        <p className="usage-dashboard-note">{organizationUsage.note}</p>
                      )}
                    </section>
                  )}

                  {usageDashboard && (
                    <section className="usage-data-panel">
                      <div className="usage-panel-heading">
                        <div>
                          <h3>Lokal app usage</h3>
                          <p>
                            Rigtig lokal tracking fra <code>openai_usage_stats</code>, opdelt i tilbud, site build og øvrige AI-kald.
                          </p>
                        </div>
                        <span>{usageDashboard.source}</span>
                      </div>

                      <div className="usage-category-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Arbejde</th>
                              <th>Periode</th>
                              <th>Kald</th>
                              <th>Total tokens</th>
                              <th>Input</th>
                              <th>Output</th>
                              <th>Kost</th>
                              <th>Sidst registreret</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[
                              { key: 'quote_generation', label: 'AI tilbud' },
                              { key: 'site_build', label: 'Site build' },
                              { key: 'other', label: 'Andre AI-kald' }
                            ].flatMap(({ key, label }) => ([
                              ['currentMonth', 'Denne måned'],
                              ['last30Days', 'Sidste 30 dage'],
                              ['allTime', 'Alle data']
                            ].map(([periodKey, periodLabel]) => ({
                              key,
                              label,
                              periodKey,
                              periodLabel,
                              usage: usageDashboard.periods[periodKey].categories[key] || {}
                            })))).map(({ key, label, periodKey, periodLabel, usage }) => (
                              <tr key={`${key}-${periodKey}`}>
                                <td data-label="Arbejde">
                                  <strong>{label}</strong>
                                  {periodKey === 'allTime' && usage.description && (
                                    <span>{usage.description}</span>
                                  )}
                                </td>
                                <td data-label="Periode">{periodLabel}</td>
                                <td data-label="Kald">{formatNumber(usage.requestCount)}</td>
                                <td data-label="Total tokens"><strong>{formatNumber(usage.totalTokens)}</strong></td>
                                <td data-label="Input">{formatNumber(usage.promptTokens)}</td>
                                <td data-label="Output">{formatNumber(usage.completionTokens)}</td>
                                <td data-label="Kost">${formatSmartUsd(usage.totalCost)}</td>
                                <td data-label="Sidst registreret">
                                  {usage.lastUsage ? new Date(usage.lastUsage).toLocaleString('da-DK') : 'Ingen data'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {usageDashboard.breakdown.length > 0 && (
                        <div className="usage-breakdown-list">
                          <h4>Største registrerede usage-typer</h4>
                          {usageDashboard.breakdown.slice(0, 6).map((row) => (
                            <div key={`${row.category}-${row.requestType}-${row.modelUsed}`} className="cost-item">
                              <span>{row.requestType} · {row.modelUsed}</span>
                              <strong>{formatNumber(row.totalTokens)} tokens · ${formatSmartUsd(row.totalCost)}</strong>
                            </div>
                          ))}
                        </div>
                      )}

                      {usageDashboard.coverageNote && (
                        <p className="usage-dashboard-note">{usageDashboard.coverageNote}</p>
                      )}
                    </section>
                  )}
                </div>
              ) : (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Indlæser AI usage data...</p>
                </div>
              )}
            </div>
          </div>
        ) : null}
        </Suspense>
      </main>
      
    </div>
    </SnackbarProvider>
  );
}

// Wrapper component with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
